// frontend/src/services/rentalService.js
import apiClient from './apiClient';

const CONTRATOS = 'contratos-alquiler';
const CLIENTES = 'clientes-alquiler';
const CONTACTOS = 'contactos-cliente-alquiler';
const RENGLONES = 'renglones-alquiler';
const UBIC = 'ubicaciones';

const normalizeList = (data) => {
  if (Array.isArray(data)) return { results: data, count: data.length };
  return data;
};

const soloLista = (data) => (Array.isArray(data) ? data : (data?.results || []));

// ─── Contratos de alquiler ────────────────────────────────────────────────────

const listar = async (params = {}) => {
  const response = await apiClient.get(`${CONTRATOS}/`, { params });
  return normalizeList(response.data);
};

const obtener = async (id) => {
  const response = await apiClient.get(`${CONTRATOS}/${id}/`);
  return response.data;
};

const crear = async (datos) => {
  const response = await apiClient.post(`${CONTRATOS}/`, datos);
  return response.data;
};

const actualizar = async (id, datos) => {
  const response = await apiClient.patch(`${CONTRATOS}/${id}/`, datos);
  return response.data;
};

const eliminar = async (id) => {
  await apiClient.delete(`${CONTRATOS}/${id}/`);
};

// ─── Transiciones del ciclo de vida ───────────────────────────────────────────
// La entrega y la devolución se confirman POR EQUIPO (renglón), no por contrato:
// un contrato puede tener un equipo entregado y dos todavía en el taller.

const confirmarEntrega = async (contratoId, datos) => {
  const response = await apiClient.post(`${CONTRATOS}/${contratoId}/confirmar-entrega/`, datos);
  return response.data;
};

const registrarDevolucion = async (contratoId, datos) => {
  const response = await apiClient.post(`${CONTRATOS}/${contratoId}/registrar-devolucion/`, datos);
  return response.data;
};

// El backend puede devolver `aviso` en la respuesta (ej. familia sin check
// list configurado): no es un error, el movimiento sí se registró.

// El cliente avisó que terminó con TODOS los equipos entregados que todavía no
// tengan su propio aviso: corta el devengo aunque el retiro demore.
const registrarCallOff = async (contratoId, datos = {}) => {
  const response = await apiClient.post(`${CONTRATOS}/${contratoId}/call-off/`, datos);
  return response.data;
};

// El cliente avisó que terminó con UN equipo puntual, sin afectar al resto.
const registrarCallOffRenglon = async (contratoId, renglonId, datos = {}) => {
  const response = await apiClient.post(
    `${CONTRATOS}/${contratoId}/renglones/${renglonId}/call-off/`, datos);
  return response.data;
};

const cancelar = async (contratoId, datos = {}) => {
  const response = await apiClient.post(`${CONTRATOS}/${contratoId}/cancelar/`, datos);
  return response.data;
};

// ─── Check lists: generación bajo demanda y diferencias ────────────────────────
// El check list de entrega/devolución se genera ANTES de confirmar el
// movimiento (botón dedicado en la pantalla), no como efecto automático de
// confirmarlo: así se puede exigir que esté finalizado antes de entregar o
// recibir el equipo.

const crearChecklistEntrega = async (contratoId, renglonId) => {
  const response = await apiClient.post(
    `${CONTRATOS}/${contratoId}/renglones/${renglonId}/crear-checklist-entrega/`);
  return response.data;
};

const crearChecklistDevolucion = async (contratoId, renglonId) => {
  const response = await apiClient.post(
    `${CONTRATOS}/${contratoId}/renglones/${renglonId}/crear-checklist-devolucion/`);
  return response.data;
};

const obtenerDiscrepancias = async (contratoId, renglonId) => {
  const response = await apiClient.get(
    `${CONTRATOS}/${contratoId}/renglones/${renglonId}/discrepancias/`);
  return response.data;
};

const generarOtcCargos = async (contratoId, renglonId) => {
  const response = await apiClient.post(
    `${CONTRATOS}/${contratoId}/renglones/${renglonId}/generar-otc-cargos/`);
  return response.data;
};

// ─── Adjuntos (contrato firmado, remitos, fotos, inspecciones) ────────────────

const adjuntar = async (contratoId, formData) => {
  const response = await apiClient.post(`${CONTRATOS}/${contratoId}/adjuntar/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

const eliminarAdjunto = async (contratoId, adjuntoId) => {
  await apiClient.delete(`${CONTRATOS}/${contratoId}/adjuntos/${adjuntoId}/`);
};

// ─── Datos de apoyo de la pantalla ────────────────────────────────────────────

const obtenerResumen = async (params = {}) => {
  const response = await apiClient.get(`${CONTRATOS}/resumen/`, { params });
  return response.data;
};

// Equipos que se pueden alquilar: excluye los comprometidos en otro contrato.
// Al editar, pasar `contrato` para que sus propios equipos sigan apareciendo.
const listarEquiposDisponibles = async (params = {}) => {
  const response = await apiClient.get(`${CONTRATOS}/equipos-disponibles/`, { params });
  return Array.isArray(response.data) ? response.data : [];
};

// ─── Clientes y contactos ─────────────────────────────────────────────────────

const listarClientes = async (params = {}) => {
  const response = await apiClient.get(`${CLIENTES}/`, { params });
  return normalizeList(response.data);
};

const obtenerCliente = async (id) => {
  const response = await apiClient.get(`${CLIENTES}/${id}/`);
  return response.data;
};

const crearCliente = async (datos) => {
  const response = await apiClient.post(`${CLIENTES}/`, datos);
  return response.data;
};

const actualizarCliente = async (id, datos) => {
  const response = await apiClient.patch(`${CLIENTES}/${id}/`, datos);
  return response.data;
};

const eliminarCliente = async (id) => {
  await apiClient.delete(`${CLIENTES}/${id}/`);
};

const listarContactos = async (clienteId) => {
  const r = await apiClient.get(`${CONTACTOS}/`, { params: { cliente: clienteId, page_size: 500 } });
  return soloLista(r.data);
};

// ─── Renglones (equipos alquilados) ───────────────────────────────────────────

const listarRenglones = async (params = {}) => {
  const response = await apiClient.get(`${RENGLONES}/`, { params });
  return normalizeList(response.data);
};

// Renglones que se pueden vincular a un traslado logístico, para el selector
// de "vincular a entrega/devolución de alquiler" del formulario de Logística.
// `equipoIds` acota a los equipos ya cargados en el traslado; `estado` es
// 'pendiente' (candidatos a entrega) o 'entregado' (candidatos a devolución).
const buscarRenglonesVinculables = async (equipoIds, estado) => {
  if (!equipoIds?.length) return [];
  const r = await apiClient.get(`${RENGLONES}/`, {
    params: { equipo__in: equipoIds.join(','), estado, page_size: 200 },
  });
  return soloLista(r.data);
};

// ─── Catálogos ────────────────────────────────────────────────────────────────
// Ubicaciones es un catálogo acotado: se trae entero, igual que en logística.

const listarUbicaciones = async () => {
  const r = await apiClient.get(`${UBIC}/?page_size=1000`);
  return soloLista(r.data);
};

const rentalService = {
  listar,
  obtener,
  crear,
  actualizar,
  eliminar,
  confirmarEntrega,
  registrarDevolucion,
  registrarCallOff,
  registrarCallOffRenglon,
  cancelar,
  crearChecklistEntrega,
  crearChecklistDevolucion,
  obtenerDiscrepancias,
  generarOtcCargos,
  adjuntar,
  eliminarAdjunto,
  obtenerResumen,
  listarEquiposDisponibles,
  listarClientes,
  obtenerCliente,
  crearCliente,
  actualizarCliente,
  eliminarCliente,
  listarContactos,
  listarRenglones,
  buscarRenglonesVinculables,
  listarUbicaciones,
};

export default rentalService;
